// Types
import { MessageType } from "../chat.type";
// React Hooks
import { useState, useEffect, ReactNode, useContext } from "react";
import { Toast as showToast } from "core/components/global/toast/toast";
// UI
import {
  MessageTextDiv,
  MessageTextPaper,
  MessageRowContentSpan,
  ChatBotFeedbackStyle,
  ChatBotTextArea,
  MessageWidgetDiv,
  MessageTextHuman,
  DialogActionsButtonContainer,
  BootstrapDialogInnerContainer,
  MessageTime,
} from "../chat.styled";
import { useTheme } from "styled-components";

import {
  Button,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import { ContentCopy, ThumbDownAlt, ThumbUpAlt } from "@mui/icons-material";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import CloseIcon from "@mui/icons-material/Close";
import { FileDownloadDone } from "@mui/icons-material";
import { BootstrapDialog } from "../chat.styled";
import { sendFeedbackToApi } from "modules/chat/services/chatService";
import { useCopyToClipboard } from "core/hooks/copytoClipboard";
import CustomMarkdown from "core/components/global/markdown/markdown";
import { SpaceInvaderCanvas } from "core/components/global/icons/spaceInvaders/spaceInvaders";
import { AuthContext, UserClaims } from "core/contexts/auth/auth";
import { formatMessageTime } from "core/utils/dateTimeUtils";
import { BootstrapDialogTitle } from "core/components/global/bootstrap/dialogTitle";

interface MessageRowProps {
  message: MessageType;
  loading: boolean;
  userPrompt: string;
  amsUser: UserClaims;
  conversationId: string;
  disableFeedback?: boolean;
  containerWidth: number;
}

export function MessageRow({
  message,
  loading,
  userPrompt,
  amsUser,
  conversationId,
  disableFeedback,
  containerWidth
}: MessageRowProps) {
  
  type CopyableContent = string | { text: string };

  const [copiedValue, setCopiedValue] = useState<CopyableContent>(() => {
    if (
      message.model === "Translate" &&
      typeof message.content === "object" &&
      "text" in message.content
    ) {
      return { text: message.content.text };
    } else if (typeof message.content === "string") {
      return message.content;
    } else {
      return "";
    }
  });

  const bgColor = message.role === "assistant" ? "#222" : "black";
  const [iscopied, setIsCopied] = useState<boolean>(false);
  const [isLiked, setIsLiked] = useState<boolean>(false);
  const [isDisliked, setIsDisliked] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [copiedText, copyToClipboard] = useCopyToClipboard();
  const { checkAMSToken, amsBearerToken } = useContext(AuthContext);
  const theme = useTheme();

  // Set message background color
  const customStyle = {
    backgroundColor: bgColor,
  };

  const handleLikeClickOpen = (disableFeedback) => {
    if (!disableFeedback) {
      setOpen(true);
      setIsLiked(true);
    }
  };

  const handleDislikeClickOpen = (disableFeedback) => {
    if (!disableFeedback) {
      setOpen(true);
      setIsDisliked(true);
    }
  };

  const handleClose = () => {
    setIsDisliked(false);
    setIsLiked(false);
    setOpen(false);
  };

  const handleSubmit = async () => {
    const token = await checkAMSToken();

    const wasSuccessful = await sendFeedbackToApi(
      token,
      conversationId,
      message,
      isLiked,
      feedbackText
    );
    if (wasSuccessful) {
      setOpen(false);
      setIsSubmitted(true);
    } else {
      showToast(
        "Error submitting a review, please reach out to an administrator",
        "error"
      );
    }
  };

  const handleChangeFeedBack = (e) => {
    const { value } = e.target;
    setFeedbackText(value);
  };

  const copyText = async () => {
    const textToCopy = String(message.content)
  
    if (textToCopy) {
      await copyToClipboard(textToCopy);
    }
  };
  // Define handlers
  const handleCopy = (disableFeedback) => {
    if (!disableFeedback) {
      // Copy the text to the clipboard
      copyText();
      // Set the copied value for the local datastore
      setCopiedValue(String(message.content));
      // Update the UI Icon with the copied img
      setIsCopied(true);
    }
  };

  useEffect(() => {
    if (iscopied) {
      // Make it return to its original state after 5 seconds
      setTimeout(() => {
        setIsCopied(false);
      }, 5000);
    } else {
      // do nothing
    }
  }, [iscopied]);

  /**
   * This function is to parse the py dictionary to a valid object
   * @param content it is mostly a response from the ai
   * @returns an object
   */
  const parseMessageContent = (content) => {
    try {
      // Language translation
      if (typeof content === "string") {
        /**
         * Clean the data before translating the Python dict ('') to a JSON object ("")
         * Content can have single quotes: Sam's dog.
         * Content can have single double quotes: Sam said "hey that's my dog".
         * Content can have double single and double double quotes if it is a table or other.
         * These situations above need to be taken care of, or the parsing as JSON will fail.
         */
        const tempStr = content
          .replace(/(\w)'(\w)/g, "$1#APOSTROPHE#$2")
          .replace(/\'"\'/g, "#QUOTE#")
          .replace(/''/g, "#DOUBLEPOSTROPHE#")
          .replace(/""/g, "#DOUBLEQUOTE#");

        // Now replace all single quotes with double quotes
        const replaced = tempStr.replace(/'/g, '"').replace(/\\xa0/g, " ");

        // Finally, restore the apostrophes
        const formattedContent = replaced
          .replace(/#APOSTROPHE#/g, "'")
          .replace(/#QUOTE#/g, "'")
          .replace(/#DOUBLEPOSTROPHE#/g, "'")
          .replace(/#DOUBLEQUOTE#/g, "'");

        // Load the cleaned data as JSON
        const jsonPayload = JSON.parse(formattedContent);

        // The response is a list of objects as the API supports multiple requests in 1 message, but we only seek to use 1 per message
        return jsonPayload[0].text;
      }
      return content;
    } catch (error) {
      return content;
    }
  };

  return (
    <div
      className={`message ${
        message.role === "user"
          ? "user justify-self-end"
          : "ai justify-self-start"
      }`}
      key={message._id}
    >
      {/* Loading Screen */}
      {loading ? (
        <MessageRowContentSpan
          className={`w-100 flex-row-reverse fade ${loading ? "visible" : "hidden"}`}
        >
          <MessageTextDiv>
            <MessageTextHuman>
              <div className="wave">
                <span className="dot" />
                <span className="dot" />
                <span className="dot" />
              </div>
            </MessageTextHuman>
          </MessageTextDiv>
        </MessageRowContentSpan>
      ) : (
        // Message history
        <>
          {message.content.toString().length > 0 && (
            <MessageRowContentSpan
              className={`fade ${loading ? "hidden" : "visible"} ${message.role === "user" && "d-flex justify-content-end"}`}
            >
              <div
                className={`d-flex align-items-center ${
                  message.role === "assistant" ? "flex-row" : "flex-row-reverse"
                }`}
              >
                {message.role !== "assistant" ? (
                  <div className="align-self-baseline">
                    {/* AI & User Icons */}
                    <SpaceInvaderCanvas text={amsUser?.email.split("@")[0] || "default"} />
                  </div>
                ) : (
                  <></>
                )}
                <div>
                  {/* Show the message */}
                  {message.role !== "assistant" ? (
                    <MessageTextPaper elevation={1}>
                      {/* <CustomMarkdown content={message.content || ""} /> */}
                      <CustomMarkdown
                        token={amsBearerToken || ''}
                        containerWidth={containerWidth}
                        conversationId={conversationId}
                        showMore={true}
                        content={(() => {
                          if (message.model === "Translate") {
                            return parseMessageContent(message.content);
                          } else {
                            return message.content || "";
                          }
                        })()}
                      />
                    </MessageTextPaper>
                  ) : (
                    <MessageTextDiv>
                      {/* Slowly write the latest AI message */}
                      {/* <CustomMarkdown content={message.content || ""}/> */}
                      <CustomMarkdown
                        token={amsBearerToken || ''}
                        containerWidth={containerWidth}
                        conversationId={conversationId}
                        showMore={true}
                        content={(() => {
                          if (message.model === "Translate") {
                            return parseMessageContent(message.content);
                          } else {
                            return message.content || "";
                          }
                        })()}
                      />

                      {/* Show AI message copy icons */}
                      <MessageWidgetDiv>
                        <ChatBotFeedbackStyle>
                          {!iscopied ? (
                            <Tooltip title="Copy" arrow className="me-2">
                              <ContentCopy
                                onClick={() => handleCopy(disableFeedback)}
                                className="ChatBot_ContentCopy"
                                elevation={1}
                                fontSize="small"
                              />
                            </Tooltip>
                          ) : (
                            <FileDownloadDone
                              className="me-2 done"
                              color="secondary"
                              elevation={1}
                              fontSize="small"
                            />
                          )}

                          {/* Feedback - Liked */}
                          {!isLiked && !isDisliked ? (
                            <Tooltip title="Like" arrow>
                              <ThumbUpOffAltIcon
                                onClick={() =>
                                  handleLikeClickOpen(disableFeedback)
                                }
                                fontSize="small"
                                elevation={1}
                              />
                            </Tooltip>
                          ) : isLiked && (
                            <ThumbUpAlt
                              className="me-2 done"
                              fontSize="small"
                              elevation={1}
                            />
                          )}

                          {/* {Feedback - DisLiked} */}
                          {!isDisliked && !isLiked ? (
                            <Tooltip title="Dislike" arrow className="ms-2">
                              <ThumbDownOffAltIcon
                                onClick={() =>
                                  handleDislikeClickOpen(disableFeedback)
                                }
                                className="me-2"
                                color="secondary"
                                fontSize="small"
                                elevation={1}
                              />
                            </Tooltip>
                          ) : isDisliked && (
                            <ThumbDownAlt
                              className="me-2 done"
                              color="secondary"
                              fontSize="small"
                              elevation={1}
                            />
                          )}

                          {/* Code for Feedback dialog box  */}
                          <BootstrapDialog
                            onClose={handleClose}
                            sx={{ borderRadius: "1rem" }}
                            open={open}
                          >
                            <BootstrapDialogInnerContainer elevation={2}>
                              <BootstrapDialogTitle
                                id="feedback-dialog-title"
                                onClose={handleClose}
                                customTheme={theme}
                              >
                                {isSubmitted
                                  ? ""
                                  : "Provide additional feedback"}
                              </BootstrapDialogTitle>
                              <DialogContent dividers>
                                {!isSubmitted && (
                                  <>
                                    <ChatBotTextArea
                                      placeholder="Write your feedback..."
                                      value={feedbackText}
                                      onChange={handleChangeFeedBack}
                                    />
                                  </>
                                )}
                              </DialogContent>
                              <DialogActionsButtonContainer>
                                {!isSubmitted && (
                                  <Button
                                    autoFocus
                                    onClick={handleSubmit}
                                    variant="contained"
                                    disabled={!feedbackText}
                                  >
                                    Submit Feedback
                                  </Button>
                                )}
                              </DialogActionsButtonContainer>
                            </BootstrapDialogInnerContainer>
                          </BootstrapDialog>
                        </ChatBotFeedbackStyle>
                      </MessageWidgetDiv>

                      {/* Show Created At */}
                      <MessageTime className="my-1">
                        {formatMessageTime(message.createdAt)}
                      </MessageTime>
                    </MessageTextDiv>
                  )}
                </div>
              </div>
            </MessageRowContentSpan>
          )}
        </>
      )}
    </div>
  );
}
